import { put, select, call, cancelled } from 'redux-saga/effects';
import { EffectR } from 'types/utils';
import log from 'loglevel';
import { api } from 'api';
import { getArticle, getEnv } from 'common/selectors';
import { FETCH_LINKED_PAGES } from './actions';
import { fetchLinkedPages } from './api';
import { getRegionId, getVisibility } from 'features/page-model/ducks/get-model';
import { LinkedPagesResponse } from '../types';

interface ApiErrorResponse {
  status: number;
  statusText?: string;
}

// NOTE: get api не обрабатывал 400 ошибку.
function isErrorResponse(response: unknown): response is ApiErrorResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'status' in response &&
    typeof (response as ApiErrorResponse).status === 'number' &&
    (response as ApiErrorResponse).status >= 400
  );
}

export function* doFetchLinkedPages() {
  const visibility: boolean = yield select(getVisibility);
  if (!visibility) {
    yield put(FETCH_LINKED_PAGES.success({ brand: { name: '' }, pages: [] }));
    return;
  }

  const productId: string = yield select(getArticle);
  const regionId: string = yield select(getRegionId);
  const abortController = (yield call([api, 'abortController'])) as EffectR.Call<
    (typeof api)['abortController']
  >;
  const { requestID } = yield select(getEnv);

  try {
    yield put(FETCH_LINKED_PAGES.request());
    logRequestStart(requestID);

    const response = (yield call(
      fetchLinkedPages,
      { productId, regionId },
      { cancelToken: abortController.token },
    )) as LinkedPagesResponse | ApiErrorResponse;

    if (isErrorResponse(response)) {
      throw new Error(`API Error ${response.status}: ${response.statusText || 'Unknown error'}`);
    }

    yield put(FETCH_LINKED_PAGES.success(response));

    logRequestSuccess(requestID);
  } catch (e) {
    const error = e instanceof Error ? e : new Error('Unknown error');
    yield put(FETCH_LINKED_PAGES.failure(error));
    logRequestError(requestID, error);
  } finally {
    if ((yield cancelled()) as boolean) {
      abortController.cancel();
      yield put(FETCH_LINKED_PAGES.cancel());
      logRequestCancel(requestID);
    }
  }
}

function logRequestStart(requestID: string) {
  log.info('fetchLinkedPages requested', {
    requestID,
    timestamp: Date.now(),
  });
}

function logRequestSuccess(requestID: string) {
  log.info('fetchLinkedPages loaded', {
    requestID,
    timestamp: Date.now(),
  });
}

function logRequestError(requestID: string, error: Error) {
  log.error('fetchLinkedPages error', {
    requestID,
    timestamp: Date.now(),
    error: error.message,
  });
}

function logRequestCancel(requestID: string) {
  log.warn('fetchLinkedPages cancelled', {
    requestID,
    timestamp: Date.now(),
  });
}
